import { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, DollarSign, BarChart3, PieChart, Activity, ArrowRight, ChevronDown, Users, ZoomIn, Target, ArrowDown, Clock, Layers, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ImageModal, PayoutCarousel } from '../components/shared';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===========================================
// STATIC DATA (Terms, Thesis - same for all)
// ===========================================

const termsSheet = {
  waterfall: {
    managementFee: '0%',
    carriedInterest: '80/20 split',
    targetToDAO: '20-25% APY',
  },
  buyTerms: {
    lockupPeriod: '12 months, 90-day notice to withdraw',
    openOrClosed: 'Open Ended',
    minimumCommitment: '€2.000',
    education: 'Starting from €5.000 Tickets',
  },
};

const thesisProcess = [
  { step: '1', title: 'Diversify', description: 'Split 50-50 Blockchain-Forex.' },
  { step: '2.1', title: 'Blockchain (50%)', description: 'Strategies Diversification: Stacking, Few Important Trades, Some intel-memes and AI (current market season).' },
  { step: '2.2', title: 'Forex (50%)', description: 'Strategies Diversification: mainly Gold buy trades and other 2 strategies on Gold and GBPUSD.' },
];

// ===========================================
// DONUT CHART COMPONENT
// ===========================================
const DonutChart = ({ segments, size = 160, strokeWidth = 24, centerText }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full transform -rotate-90">
        {segments.map((segment, index) => {
          const strokeDasharray = (segment.value / 100) * circumference;
          const strokeDashoffset = -offset;
          offset += strokeDasharray;
          return (
            <circle key={index} cx={size / 2} cy={size / 2} r={radius} fill="transparent" stroke={segment.color} strokeWidth={strokeWidth} strokeDasharray={`${strokeDasharray} ${circumference}`} strokeDashoffset={strokeDashoffset} />
          );
        })}
      </svg>
      {centerText && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-white">{centerText}</span>
        </div>
      )}
    </div>
  );
};

// ===========================================
// PAGE COMPONENT
// ===========================================
const QuantitativeResultsPage = () => {
  const { t } = useLanguage();
  const [selectedView, setSelectedView] = useState('portfolio');
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalImage, setModalImage] = useState({ isOpen: false, src: '', alt: '' });
  const [loading, setLoading] = useState(true);
  const [clientsData, setClientsData] = useState([]);
  const [blockchainTrades, setBlockchainTrades] = useState([]);
  const [forexTrades, setForexTrades] = useState([]);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/results/public-data`);
        const data = await res.json();
        setClientsData(data.clients || []);
        setBlockchainTrades(data.blockchain_trades || []);
        setForexTrades(data.forex_trades || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const openModal = (src, alt) => setModalImage({ isOpen: true, src, alt });
  const closeModal = () => setModalImage({ isOpen: false, src: '', alt: '' });

  // Get selected client (or all for portfolio)
  const selectedClient = selectedView === 'portfolio' ? null : clientsData.find(c => c.id === selectedView);
  const clientAmount = selectedClient ? selectedClient.initial_amount : clientsData.reduce((sum, c) => sum + c.initial_amount, 0);

  // Helper to parse date string (MM/YYYY or DD/MM/YYYY) to comparable format
  const parseDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 2) {
      // MM/YYYY format
      return new Date(parseInt(parts[1]), parseInt(parts[0]) - 1, 1);
    } else if (parts.length === 3) {
      // DD/MM/YYYY format
      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
    return null;
  }, []);

  // Filter trades by client_id OR by client's start date (trade must open AFTER client started)
  const filterTradesForClient = (trades, client) => {
    if (!client) {
      // Portfolio view: ONLY show global trades (no client_id), exclude client-specific ones like AG's
      return trades.filter(trade => !trade.client_id);
    }
    
    return trades.filter(trade => {
      // If trade has a specific client_id, only show to that client
      if (trade.client_id) {
        return trade.client_id === client.id;
      }
      
      // Otherwise, filter by date: trade must OPEN after client started
      const clientStart = parseDate(client.start_date);
      const clientEnd = client.end_date ? parseDate(client.end_date) : new Date();
      const tradeOpen = parseDate(trade.date_open);
      
      if (!tradeOpen || !clientStart) return true;
      // Trade must OPEN within client's active period (not before they started!)
      return tradeOpen >= clientStart && tradeOpen <= clientEnd;
    });
  };

  // Get filtered trades for the selected view, sorted by date open (entry)
  const sortByDateOpen = (trades) => {
    return [...trades].sort((a, b) => {
      const dateA = parseDate(a.date_open);
      const dateB = parseDate(b.date_open);
      return (dateA || new Date(0)) - (dateB || new Date(0));
    });
  };

  const filteredBlockchainTrades = sortByDateOpen(filterTradesForClient(blockchainTrades, selectedClient));
  const filteredForexTrades = sortByDateOpen(filterTradesForClient(forexTrades, selectedClient));

  // Calculate P&L for each trade based on client's portfolio
  const calculateTradeValues = (trade) => {
    // For portfolio view: calculate based on average client amount to avoid inflated totals
    const baseAmount = selectedClient ? selectedClient.initial_amount : (clientsData.length > 0 ? clientAmount / clientsData.length : 0);
    const riskedAmount = (trade.risked_percent / 100) * baseAmount;
    const pnl = riskedAmount * trade.rr_ratio;
    // ROI as percentage of initial capital = risk% * R:R ratio
    const roiPercent = trade.risked_percent * trade.rr_ratio;
    return { riskedAmount, pnl, roiPercent };
  };

  // Calculate totals based on filtered trades
  const blockchainTotal = filteredBlockchainTrades.reduce((sum, t) => sum + calculateTradeValues(t).pnl, 0);
  const forexTotal = filteredForexTrades.reduce((sum, t) => sum + calculateTradeValues(t).pnl, 0);
  
  // Total Return % = Sum of all individual ROIs (risk% * R:R for each trade)
  const blockchainROI = filteredBlockchainTrades.reduce((sum, t) => sum + calculateTradeValues(t).roiPercent, 0);
  const forexROI = filteredForexTrades.reduce((sum, t) => sum + calculateTradeValues(t).roiPercent, 0);
  const totalReturn = blockchainROI + forexROI;
  
  // Calculate Portfolio totals (sum across all clients)
  const calculatePortfolioTotals = () => {
    let totalInvested = 0;
    let totalPnL = 0;
    
    clientsData.forEach(client => {
      const clientBlockchain = filterTradesForClient(blockchainTrades, client);
      const clientForex = filterTradesForClient(forexTrades, client);
      
      const clientPnL = [...clientBlockchain, ...clientForex].reduce((sum, trade) => {
        const riskedAmount = (trade.risked_percent / 100) * client.initial_amount;
        return sum + (riskedAmount * trade.rr_ratio);
      }, 0);
      
      totalInvested += client.initial_amount;
      totalPnL += clientPnL;
    });
    
    return { invested: totalInvested, pnl: totalPnL };
  };
  
  const portfolioTotals = calculatePortfolioTotals();

  // For single client view, calculate totals
  const calculateClientTotals = () => {
    if (!selectedClient) return null; // Portfolio view - no totals
    return {
      invested: selectedClient.initial_amount,
      pnl: blockchainTotal + forexTotal,
      currentValue: selectedClient.initial_amount + blockchainTotal + forexTotal,
    };
  };
  
  const clientTotals = calculateClientTotals();
  const isPortfolioView = !selectedClient;

  // Generate ROI chart data based on trades over time (sorted by date_open)
  const chartData = useMemo(() => {
    const baseAmount = selectedClient ? selectedClient.initial_amount : (clientsData.length > 0 ? clientsData.reduce((sum, c) => sum + c.initial_amount, 0) / clientsData.length : 0);
    
    // Combine blockchain + forex trades and re-sort chronologically by date_open
    const allTrades = [...filteredBlockchainTrades, ...filteredForexTrades].sort((a, b) => {
      const dateA = parseDate(a.date_open);
      const dateB = parseDate(b.date_open);
      return (dateA || new Date(0)) - (dateB || new Date(0));
    });

    if (allTrades.length === 0 || baseAmount === 0) {
      return [];
    }

    // Calculate cumulative ROI over time
    let cumulativePnL = 0;
    const data = [{ date: 'Start', roi: 0, pnl: 0 }];

    allTrades.forEach((trade) => {
      const riskedAmount = (trade.risked_percent / 100) * baseAmount;
      const tradePnL = riskedAmount * trade.rr_ratio;
      cumulativePnL += tradePnL;
      
      data.push({
        date: trade.date_open || 'Unknown',
        roi: parseFloat(((cumulativePnL / baseAmount) * 100).toFixed(2)),
        pnl: parseFloat(cumulativePnL.toFixed(2)),
        asset: trade.asset,
        // Track if this is cumulative ROI or just the trade ROI
        tradeRoi: trade.risked_percent * trade.rr_ratio,
      });
    });

    return data;
  }, [filteredBlockchainTrades, filteredForexTrades, selectedClient, clientsData]);

  // Custom tooltip for the chart - shows daily change in Portfolio view
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const dailyChange = data.tradeRoi || 0;
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-cyan-400 font-medium text-sm">{label}</p>
          {data.asset && <p className="text-gray-400 text-xs mb-1">{data.asset}</p>}
          <p className="text-white text-sm">
            Cumulative ROI: <span className={data.roi >= 0 ? 'text-green-400' : 'text-red-400'}>{data.roi >= 0 ? '+' : ''}{data.roi}%</span>
          </p>
          {/* Daily performance indicator - always show for trades */}
          {data.tradeRoi !== undefined && label !== 'Start' && (
            <p className="text-white text-sm mt-1 pt-1 border-t border-gray-700">
              Daily: <span className={`font-bold ${dailyChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {dailyChange >= 0 ? '+' : ''}{dailyChange.toFixed(2)}%
              </span>
            </p>
          )}
          {!isPortfolioView && (
            <p className="text-white text-sm">P&L: <span className={data.pnl >= 0 ? 'text-green-400' : 'text-red-400'}>€{data.pnl.toFixed(2)}</span></p>
          )}
        </div>
      );
    }
    return null;
  };

  const mainAllocation = [
    { label: 'Forex', value: 50, color: '#06b6d4' },
    { label: 'Blockchain', value: 50, color: '#8b5cf6' },
  ];
  const blockchainAllocation = [
    { label: 'High-caps', value: 30, color: '#4f46e5' },
    { label: 'Mid-caps', value: 30, color: '#7c3aed' },
    { label: 'Low-caps', value: 30, color: '#06b6d4' },
    { label: 'Moonshots', value: 10, color: '#9ca3af' },
  ];
  const forexAllocation = [
    { label: 'XAU/USD', value: 60, color: '#06b6d4' },
    { label: 'GBP/USD', value: 25, color: '#0891b2' },
    { label: 'Other', value: 15, color: '#164e63' },
  ];

  const [searchClient, setSearchClient] = useState('');
  
  const viewOptions = [
    { id: 'portfolio', name: 'Total Portfolio', subtitle: 'All Clients', isPortfolio: true },
    ...clientsData.map(c => ({ id: c.id, name: c.name, subtitle: c.end_date ? `${c.start_date} - ${c.end_date}` : `Since ${c.start_date}`, isPortfolio: false }))
  ];
  const filteredOptions = viewOptions.filter(opt => 
    opt.isPortfolio || opt.name.toLowerCase().includes(searchClient.toLowerCase())
  );
  const selectedOption = viewOptions.find(v => v.id === selectedView);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="quant-results-page">
      <Navbar />
      <ImageModal isOpen={modalImage.isOpen} onClose={closeModal} imageSrc={modalImage.src} imageAlt={modalImage.alt} />
      
      {/* Header - Compact */}
      <section className="pt-20 pb-4 md:pt-24 md:pb-6 px-4 relative">
        <div className="max-w-6xl mx-auto">
          <p className="text-cyan-400 text-sm tracking-[0.3em] mb-2">{t('results.dashboard')}</p>
          <h1 className="text-3xl md:text-5xl font-bold mb-3">{t('results.title')}</h1>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl">{t('results.subtitle')}</p>
        </div>
        {/* Subtle background glow - decorative */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>
      </section>

      {/* View Selector */}
      <section className="px-4 pb-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
            <Users size={18} className="text-cyan-400 flex-shrink-0" />
            <div className="relative flex-1 md:flex-none">
              <button onClick={() => { setShowDropdown(!showDropdown); setSearchClient(''); }} className="w-full md:w-auto flex items-center justify-between md:justify-start gap-2 bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-sm hover:border-cyan-500/50 transition-colors" data-testid="view-selector">
                <span className="font-medium text-white">{selectedOption?.name}</span>
                <span className="text-gray-400 text-xs hidden md:inline">({selectedOption?.subtitle})</span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 md:right-auto mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-2xl z-50 min-w-full md:min-w-[320px] max-h-[400px] overflow-hidden flex flex-col">
                  {/* Portfolio Option - Highlighted */}
                  <button 
                    onClick={() => { setSelectedView('portfolio'); setShowDropdown(false); }} 
                    className={`w-full px-4 py-4 text-left border-b border-gray-700 hover:bg-cyan-900/30 transition-colors ${selectedView === 'portfolio' ? 'bg-cyan-900/40' : 'bg-gray-700/50'}`}
                  >
                    <div className="flex items-center gap-2">
                      <PieChart size={16} className="text-cyan-400" />
                      <span className="font-bold text-cyan-400">{t('results.totalPortfolio')}</span>
                    </div>
                    <div className="text-gray-400 text-xs mt-1">{t('results.aggregatedView')}</div>
                  </button>
                  
                  {/* Search Input */}
                  {clientsData.length > 3 && (
                    <div className="p-2 border-b border-gray-700 bg-gray-800">
                      <input
                        type="text"
                        placeholder={t('results.searchClient')}
                        value={searchClient}
                        onChange={(e) => setSearchClient(e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                  
                  {/* Client Options */}
                  <div className="overflow-y-auto max-h-[250px] bg-gray-800">
                    {filteredOptions.filter(opt => !opt.isPortfolio).map((option) => (
                      <button 
                        key={option.id} 
                        onClick={() => { setSelectedView(option.id); setShowDropdown(false); }} 
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-700 transition-colors ${selectedView === option.id ? 'bg-gray-700 text-cyan-400' : 'text-white'}`}
                      >
                        <div className="font-medium">{option.name}</div>
                        <div className="text-gray-400 text-xs">{option.subtitle}</div>
                      </button>
                    ))}
                    {filteredOptions.filter(opt => !opt.isPortfolio).length === 0 && (
                      <div className="px-4 py-3 text-gray-400 text-sm text-center">{t('results.noClientsFound')}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Helper text - to the right of dropdown on desktop */}
            <p className="text-gray-500 text-sm italic hidden md:block">{t('results.selectHelper')}</p>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          {isPortfolioView ? (
            // Portfolio View - Show only Total Invested and Total Return, right aligned on desktop
            <div className="flex justify-center md:justify-end">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full max-w-3xl">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6 flex flex-col">
                  <div className="flex-1 flex items-center justify-end">
                    <p className="text-2xl md:text-3xl font-bold">€{portfolioTotals.invested.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-800"><DollarSign size={16} className="text-cyan-400" /><span className="text-gray-400 text-xs md:text-sm">{t('results.totalInvested')}</span></div>
                </div>
                {/* Total Return Card - Premium Style */}
                <div className="bg-gradient-to-br from-gray-900 via-cyan-950/40 to-gray-900 border border-cyan-700/50 rounded-xl p-4 md:p-6 relative overflow-hidden flex flex-col">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-cyan-500/20 rounded-full blur-2xl"></div>
                  <div className="flex-1 flex items-center justify-end relative z-10">
                    <p className={`text-2xl md:text-3xl font-bold ${totalReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>{totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(2)}%</p>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-cyan-700/30 relative z-10">
                    <BarChart3 size={16} className="text-cyan-400" /><span className="text-gray-400 text-xs md:text-sm">{t('results.totalReturn')}</span>
                  </div>
                  <p className="absolute bottom-1.5 left-6 text-[9px] text-cyan-400/50">Sum of all trade ROIs (Σ Risk × R:R)</p>
                </div>
                {/* Annual Target Card */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6 flex flex-col col-span-2 md:col-span-1" data-testid="annual-target-card">
                  <div className="flex-1 flex items-center justify-end">
                    <p className="text-2xl md:text-3xl font-bold text-cyan-400">20-25%</p>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-800"><Target size={16} className="text-cyan-400" /><span className="text-gray-400 text-xs md:text-sm">{t('results.annualTarget')}</span></div>
                </div>
              </div>
            </div>
          ) : (
            // Client View - Show all stats, full width
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6 flex flex-col">
                <div className="flex-1 flex items-center justify-end">
                  <p className="text-2xl md:text-3xl font-bold">€{clientTotals?.invested.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-800"><DollarSign size={16} className="text-cyan-400" /><span className="text-gray-400 text-xs md:text-sm">{t('results.totalInvested')}</span></div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6 flex flex-col">
                <div className="flex-1 flex items-center justify-end">
                  <p className={`text-2xl md:text-3xl font-bold ${clientTotals?.currentValue >= clientTotals?.invested ? 'text-green-400' : 'text-red-400'}`}>€{clientTotals?.currentValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-800"><TrendingUp size={16} className="text-cyan-400" /><span className="text-gray-400 text-xs md:text-sm">{t('results.currentValue')}</span></div>
              </div>
              {/* Total Return Card - Premium Style */}
              <div className="bg-gradient-to-br from-gray-900 via-cyan-950/40 to-gray-900 border border-cyan-700/50 rounded-xl p-4 md:p-6 relative overflow-hidden flex flex-col">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-cyan-500/20 rounded-full blur-2xl"></div>
                <div className="flex-1 flex items-center justify-end relative z-10">
                  <p className={`text-2xl md:text-3xl font-bold ${totalReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>{totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(2)}%</p>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-cyan-700/30 relative z-10">
                  <BarChart3 size={16} className="text-cyan-400" /><span className="text-gray-400 text-xs md:text-sm">{t('results.totalReturn')}</span>
                </div>
                <p className="absolute bottom-1.5 left-6 text-[9px] text-cyan-400/50">Sum of all trade ROIs (Σ Risk × R:R)</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6 flex flex-col">
                <div className="flex-1 flex items-center justify-end">
                  <p className={`text-2xl md:text-3xl font-bold ${clientTotals?.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>€{Math.abs(clientTotals?.pnl || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-800"><Activity size={16} className="text-cyan-400" /><span className="text-gray-400 text-xs md:text-sm">{t('results.realizedGains')}</span></div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ROI Chart - ABOVE pie charts */}
      <section className="px-2 md:px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl font-bold">{t('results.roiEvolution')}</h2>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-cyan-400 rounded-full"></span>
                <span className="text-gray-400 text-xs md:text-sm">{t('results.cumulativeRoi')}</span>
              </div>
            </div>
            <div className="h-64 md:h-80 -ml-2 md:ml-0 select-none" style={{ WebkitTapHighlightColor: 'transparent', WebkitTouchCallout: 'none', userSelect: 'none' }}>
              {chartData.length > 1 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 5, left: -15, bottom: 0 }}>
                    <defs>
                      <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6b7280" 
                      tick={{ fill: '#9ca3af', fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      stroke="#6b7280" 
                      tick={{ fill: '#9ca3af', fontSize: 11 }}
                      tickFormatter={(value) => `${value}%`}
                      width={45}
                    />
                    <Tooltip 
                      content={<CustomTooltip />} 
                      cursor={false}
                      wrapperStyle={{ outline: 'none', zIndex: 50, background: 'transparent', border: 'none', boxShadow: 'none' }}
                      allowEscapeViewBox={{ x: false, y: false }}
                      isAnimationActive={false}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="roi" 
                      stroke="#06b6d4" 
                      strokeWidth={2}
                      fill="url(#roiGradient)"
                      dot={{ fill: '#06b6d4', strokeWidth: 2, stroke: '#1f2937', r: 3 }}
                      activeDot={false}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full bg-gray-800/50 rounded-lg flex items-center justify-center border border-gray-700 border-dashed">
                  <div className="text-center text-gray-500">
                    <BarChart3 size={48} className="mx-auto mb-2 opacity-50" />
                    <p>{t('results.noData')}</p>
                    <p className="text-xs">{t('results.addTrades')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Strategy - 3 Diversification Layers */}
      <section className="px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="relative">
              <img src="https://static.wixstatic.com/media/7a40f4_f757f5025cc04abf83177ff052676c58~mv2.jpg" alt="Strategy Background" className="w-full h-24 md:h-32 object-cover opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70 flex items-center px-6">
                <h2 className="text-2xl md:text-3xl font-bold">Strategy — "3 Diversification Layers"</h2>
              </div>
            </div>
            
            <div className="p-6 md:p-8">
              <p className="text-gray-400 mb-8 max-w-3xl mx-auto italic text-center">
                Our investment thesis is built on three layers of diversification — spanning markets, timeframes, and strategies — to maximize returns while systematically mitigating downside risk.
              </p>

              {/* Layer 1 - Two Markets */}
              <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <PieChart size={20} className="text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Diversification Layer 1 — Two Markets</h3>
                </div>
                
                {/* 3 Charts in row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Main 50/50 */}
                  <div className="flex flex-col items-center">
                    <p className="text-gray-400 text-xs tracking-wider mb-3">ALLOCATION</p>
                    <DonutChart segments={mainAllocation} size={160} strokeWidth={24} centerText="50/50" />
                    <div className="flex gap-4 mt-4">
                      {mainAllocation.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                          <span className="text-sm text-gray-300">{item.label}</span>
                          <span className="text-sm font-bold">{item.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Blockchain Sub */}
                  <div className="flex flex-col items-center">
                    <p className="text-purple-400 text-xs tracking-wider mb-3">BLOCKCHAIN</p>
                    <DonutChart segments={blockchainAllocation} size={140} strokeWidth={20} />
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-4">
                      {blockchainAllocation.map((item, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></span>
                          <span className="text-xs text-gray-400">{item.label}</span>
                          <span className="text-xs font-bold">{item.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Forex Sub */}
                  <div className="flex flex-col items-center">
                    <p className="text-cyan-400 text-xs tracking-wider mb-3">FOREX</p>
                    <DonutChart segments={forexAllocation} size={140} strokeWidth={20} />
                    <div className="flex flex-col gap-1 mt-4">
                      {forexAllocation.map((item, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></span>
                          <span className="text-xs text-gray-400">{item.label}</span>
                          <span className="text-xs font-bold">{item.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center my-3">
                <ArrowDown size={24} className="text-cyan-400/70" />
              </div>

              {/* Layer 2 - Timeframes */}
              <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Clock size={16} className="text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Diversification Layer 2 — Timeframes</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-11">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></span>
                    <span className="text-purple-400 text-sm">Blockchain</span>
                    <span className="text-gray-500 text-sm">— Swings: days, weeks, months</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0"></span>
                    <span className="text-cyan-400 text-sm">Forex</span>
                    <span className="text-gray-500 text-sm">— Daily Trades and Swings</span>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center my-3">
                <ArrowDown size={24} className="text-cyan-400/70" />
              </div>

              {/* Layer 3 - Sub-Portfolios */}
              <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Layers size={16} className="text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Diversification Layer 3 — Sub-Portfolios & Strategies</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-11">
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0 mt-1.5"></span>
                    <p className="text-sm"><span className="text-purple-400">Blockchain</span> <span className="text-gray-500">— Cascade % with sub-portfolios. Large/mid/low caps, alpha, moonshots.</span></p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0 mt-1.5"></span>
                    <p className="text-sm"><span className="text-cyan-400">Forex</span> <span className="text-gray-500">— Different systems: FV, Inst, z2z, Gold-MM...</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blockchain Trades Table */}
      <section className="px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                Blockchain Trades
              </h2>
              {!isPortfolioView && (
                <div className={`text-lg font-bold ${blockchainTotal > 0 ? 'text-green-400' : blockchainTotal < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                  Total: {blockchainTotal > 0 ? '+' : ''}{blockchainTotal.toFixed(0)}€
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Asset</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Type</th>
                    {!isPortfolioView && <th className="text-left py-3 px-2 text-gray-400 font-medium">P&L</th>}
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">ROI</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Date Open</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Entry</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Date Close</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Exit</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Contract</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlockchainTrades.map((trade) => {
                    const { pnl, roiPercent } = calculateTradeValues(trade);
                    return (
                      <tr key={trade.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="py-3 px-2 font-medium">{trade.asset}</td>
                        <td className="py-3 px-2"><span className={`px-2 py-1 rounded text-xs ${trade.trade_type === 'Long' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{trade.trade_type}</span></td>
                        {!isPortfolioView && <td className={`py-3 px-2 font-bold ${pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-gray-400'}`}>{pnl > 0 ? '+' : ''}{pnl.toFixed(0)}€</td>}
                        <td className={`py-3 px-2 ${roiPercent > 0 ? 'text-green-400' : roiPercent < 0 ? 'text-red-400' : 'text-gray-400'}`}>{roiPercent > 0 ? '+' : ''}{roiPercent.toFixed(2)}%</td>
                        <td className="py-3 px-2 text-gray-400">{trade.date_open}</td>
                        <td className="py-3 px-2">{trade.entry_price}</td>
                        <td className="py-3 px-2 text-gray-400">{trade.date_close}</td>
                        <td className="py-3 px-2">{trade.exit_price}</td>
                        <td className="py-3 px-2">{trade.contract ? (
                          <button 
                            onClick={() => navigator.clipboard.writeText(trade.contract)} 
                            className="text-gray-400 hover:text-white text-xs font-mono bg-gray-800 px-2 py-1 rounded flex items-center gap-1 group"
                            title="Click to copy"
                          >
                            <span className="truncate max-w-[120px]">{trade.contract}</span>
                            <span className="opacity-0 group-hover:opacity-100 text-cyan-400">📋</span>
                          </button>
                        ) : <span className="text-gray-600">—</span>}</td>
                        <td className="py-3 px-2">{trade.source_url ? (
                          <a href={trade.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs">🔗 Link</a>
                        ) : <span className="text-gray-600">—</span>}</td>
                      </tr>
                    );
                  })}
                  {filteredBlockchainTrades.length === 0 && <tr><td colSpan={isPortfolioView ? 9 : 10} className="py-4 text-center text-gray-500">No trades for this period</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Forex Trades Table */}
      <section className="px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="w-3 h-3 bg-cyan-400 rounded-full"></span>
                Forex Trades
              </h2>
              {!isPortfolioView && (
                <div className={`text-lg font-bold ${forexTotal > 0 ? 'text-green-400' : forexTotal < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                  Total: {forexTotal > 0 ? '+' : ''}{forexTotal.toFixed(0)}€
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Asset</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Type</th>
                    {!isPortfolioView && <th className="text-left py-3 px-2 text-gray-400 font-medium">P&L</th>}
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">ROI</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Date Open</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Entry</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Date Close</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Exit</th>
                    <th className="text-left py-3 px-2 text-gray-400 font-medium">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredForexTrades.map((trade) => {
                    const { pnl, roiPercent } = calculateTradeValues(trade);
                    return (
                      <tr key={trade.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="py-3 px-2 font-medium">{trade.asset}</td>
                        <td className="py-3 px-2"><span className={`px-2 py-1 rounded text-xs ${trade.trade_type === 'Buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{trade.trade_type}</span></td>
                        {!isPortfolioView && <td className={`py-3 px-2 font-bold ${pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-gray-400'}`}>{pnl > 0 ? '+' : ''}{pnl.toFixed(0)}€</td>}
                        <td className={`py-3 px-2 ${roiPercent > 0 ? 'text-green-400' : roiPercent < 0 ? 'text-red-400' : 'text-gray-400'}`}>{roiPercent > 0 ? '+' : ''}{roiPercent.toFixed(2)}%</td>
                        <td className="py-3 px-2 text-gray-400">{trade.date_open}</td>
                        <td className="py-3 px-2">€{trade.entry_price}</td>
                        <td className="py-3 px-2 text-gray-400">{trade.date_close}</td>
                        <td className="py-3 px-2">€{trade.exit_price}</td>
                        <td className="py-3 px-2">{trade.source_url ? (
                          <a href={trade.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs">🔗 Link</a>
                        ) : <span className="text-gray-600">—</span>}</td>
                      </tr>
                    );
                  })}
                  {filteredForexTrades.length === 0 && <tr><td colSpan={isPortfolioView ? 8 : 9} className="py-4 text-center text-gray-500">No trades for this period</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Thesis Process */}
      <section className="px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Target size={20} className="text-cyan-400" />
              Quantitative DAO Thesis Process
            </h2>
            <div className="space-y-4">
              {thesisProcess.map((item, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-cyan-400 font-bold text-sm">{item.step}</span>
                  </div>
                  <div className="pt-2">
                    <span className="font-bold text-white">{item.title}:</span>
                    <span className="text-gray-400 ml-2">{item.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Terms Sheet - ABOVE Verified Results, redesigned */}
      <section className="px-4 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="relative">
              <img src="https://static.wixstatic.com/media/7a40f4_f757f5025cc04abf83177ff052676c58~mv2.jpg" alt="Terms Background" className="w-full h-20 md:h-24 object-cover opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-800/95 flex items-center px-6">
                <h2 className="text-2xl md:text-3xl font-bold text-white">Terms Sheet</h2>
              </div>
            </div>
            
            <div className="p-6 md:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Waterfall Breakdown */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-cyan-400 mb-5 tracking-wide">WATERFALL BREAKDOWN</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-400">Management Fee</span>
                      <span className="font-bold text-white">{termsSheet.waterfall.managementFee}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-400">Carried Interest</span>
                      <span className="font-bold text-white">{termsSheet.waterfall.carriedInterest}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-400">Target to DAO Participants</span>
                      <span className="font-bold text-green-400">{termsSheet.waterfall.targetToDAO}</span>
                    </div>
                  </div>
                </div>

                {/* Buy Terms */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-cyan-400 mb-5 tracking-wide">BUY TERMS / LENGTH</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-400">Lockup Period</span>
                      <span className="font-bold text-white text-right text-sm">{termsSheet.buyTerms.lockupPeriod}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-400">Open or Closed</span>
                      <span className="font-bold text-white">{termsSheet.buyTerms.openOrClosed}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-400">Minimum Commitment</span>
                      <span className="font-bold text-white">{termsSheet.buyTerms.minimumCommitment}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-400">1on1 Education Offered</span>
                      <span className="font-bold text-white">{termsSheet.buyTerms.education}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VERIFIED RESULTS - Using shared PayoutCarousel */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] text-cyan-400 mb-3">VERIFIED RESULTS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Proof Over Promises</h2>
            <p className="text-gray-400 max-w-3xl mx-auto mb-4">
              Some of our verified trading results. Less than <span className="text-cyan-400 font-semibold">1% of traders worldwide</span> achieve consistent profitability. Our founder is ranked among the <span className="text-cyan-400 font-semibold">top traders in Spain and globally</span> on FundingPips.
            </p>
            <p className="text-gray-500 text-sm italic">*These are examples from one prop firm. Results achieved across multiple firms and strategies.</p>
          </div>

          {/* Shared PayoutCarousel Component */}
          <PayoutCarousel openModal={openModal} />

          {/* Top Trader Rankings */}
          <div className="max-w-7xl mx-auto">
            <p className="text-center text-gray-400 text-sm mb-8">Ranked #1 on multiple prop firm leaderboards</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 cursor-pointer group hover:border-cyan-500/50 transition-all" onClick={() => openModal('https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/vlx3qn4e_Captura.PNG', 'FundingPips #1 Ranking')}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-cyan-500 text-black text-lg font-bold px-4 py-2 rounded">#1</span>
                    <span className="text-white font-bold text-xl">Top Trader</span>
                  </div>
                  <div className="bg-cyan-500/20 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-lg">
                  <img src="https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/vlx3qn4e_Captura.PNG" alt="FundingPips #1 Ranking" className="w-full rounded-lg border border-gray-600 transition-transform duration-500 group-hover:scale-[1.02]" />
                  <div className="absolute top-4 left-4 bg-cyan-500 text-black text-sm font-bold px-3 py-1 rounded-full shadow-lg">#1 RANK</div>
                </div>
                <p className="text-gray-400 text-sm mt-4 text-center">FundingPips Leaderboard • Click to enlarge</p>
              </div>
              <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 cursor-pointer group hover:border-cyan-500/50 transition-all" onClick={() => openModal('https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/d6cyampb_Captura%20%284%29.PNG', 'SwiftFunding #1 Ranking')}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-cyan-500 text-black text-lg font-bold px-4 py-2 rounded">#1</span>
                    <span className="text-white font-bold text-xl">Top Trader</span>
                  </div>
                  <div className="bg-cyan-500/20 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-lg">
                  <img src="https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/d6cyampb_Captura%20%284%29.PNG" alt="SwiftFunding #1 Ranking" className="w-full rounded-lg border border-gray-600 transition-transform duration-500 group-hover:scale-[1.02]" />
                  <div className="absolute top-4 left-4 bg-cyan-500 text-black text-sm font-bold px-3 py-1 rounded-full shadow-lg">#1 RANK</div>
                </div>
                <p className="text-gray-400 text-sm mt-4 text-center">SwiftFunding Leaderboard • Click to enlarge</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pt-8 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-gray-900 via-cyan-950/30 to-gray-900 border border-cyan-700/40 rounded-2xl p-10 md:p-14 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-cyan-600/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <p className="text-cyan-400 text-sm tracking-[0.2em] mb-3">JOIN THE DAO</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Ready to Invest?</h2>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto text-lg">Join AAC Quantitative DAO and benefit from our data-driven trading strategies with verified results.</p>
              <a href="/quantitative" className="inline-flex items-center bg-gradient-to-r from-cyan-500 to-cyan-400 text-black px-10 py-4 font-bold hover:from-cyan-400 hover:to-cyan-300 transition-all rounded-lg shadow-lg shadow-cyan-500/25 text-lg" data-testid="learn-more-btn">
                Learn More
                <ArrowRight className="ml-3" size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuantitativeResultsPage;
